/**
 * The hands-on tutorial: it takes over the whole tool and walks the user through a scripted street
 * (OnboardingStates), so it is handed nearly every control.
 * Todo. So many dependencies! If possible, break the module down into pieces.
 */

import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { util } from '../../common/utilities.js';
import { Label } from '../label/Label.js';
import '../../common/pano-viewer/panoUtilities.js';
import '../../common/utilitiesMath.js';
import '../../common/utilitiesSidewalk.js';
/** @typedef {import('../canvas/CanvasFrame.js').CanvasFrame} CanvasFrame */
/** @typedef {import('../navigation/Compass.js').Compass} Compass */
/** @typedef {import('../canvas/Canvas.js').Canvas} Canvas */
/** @typedef {import('../canvas/ContextMenu.js').ContextMenu} ContextMenu */
/** @typedef {import('../Main.js').ExploreUi} ExploreUi */
/** @typedef {import('../data/Form.js').Form} Form */
/** @typedef {import('../label/LabelContainer.js').LabelContainer} LabelContainer */
/** @typedef {import('../navigation/Minimap.js').Minimap} Minimap */
/** @typedef {import('../mission/MissionContainer.js').MissionContainer} MissionContainer */
/** @typedef {import('../panorama/NavArrows.js').NavArrows} NavArrows */
/** @typedef {import('../navigation/NavigationService.js').NavigationService} NavigationService */
/** @typedef {import('../navigation/ObservedArea.js').ObservedArea} ObservedArea */
/** @typedef {import('../panorama/PanoManager.js').PanoManager} PanoManager */
/** @typedef {import('../controls/PanoOverlayControls.js').PanoOverlayControls} PanoOverlayControls */
/** @typedef {import('../../common/ProgressBar.js').ProgressBar} ProgressBar */
/** @typedef {import('../menu/LabelModes.js').LabelModes} LabelModes */
/** @typedef {import('../menu/RibbonMenu.js').RibbonMenu} RibbonMenu */
/** @typedef {import('../data/Tracker.js').Tracker} Tracker */
/** @typedef {import('../zoom/ZoomControl.js').ZoomControl} ZoomControl */

export class Onboarding {
  // The tutorial pano's size in px, and how its scripted image coordinates scale to it.
  static TUTORIAL_PANO_WIDTH = 13312;
  static TUTORIAL_PANO_HEIGHT = 6656;
  static TUTORIAL_PANO_SCALE_FACTOR = 3.25;

  #ui;
  #frame;
  #minimap;
  #panoManager;
  #navArrows;
  #compass;
  #navigationService;
  #missionContainer;
  #labelContainer;
  #observedArea;
  #form;
  #missionProgressBar;
  #panoOverlayControls;
  #labelModes;
  #ribbon;
  #tracker;
  #canvas;
  #uiCanvas;
  #contextMenu;
  #uiOnboarding;
  #zoomControl;

  #ctx;
  #blinkTimer = 0;
  #blinkFunctionIdentifier = [];
  #labelIntroRafId = null;
  #states;
  #statesWithProgress;
  #savedAnnotations = [];
  #currentState = null;
  #mouseDownCanvasDrawingHandler;
  #tutorialMinimapResizeObserver = null;

  // Floating UI autoUpdate cleanup for the currently anchored message, if any.
  #floatingCleanup = null;

  /**
   * @param {ExploreUi} ui - The tool's elements; the tutorial draws on its own canvas and over the minimap.
   * @param {CanvasFrame} frame - The frame the example labels are projected through.
   * @param {{get: () => Array<Record<string, any>>}} onboardingStates - Built by `new OnboardingStates()`.
   * @param {Minimap} minimap
   * @param {PanoManager} panoManager
   * @param {NavArrows} navArrows
   * @param {Compass} compass
   * @param {NavigationService} navigationService
   * @param {MissionContainer} missionContainer
   * @param {LabelContainer} labelContainer
   * @param {ObservedArea} observedArea
   * @param {Form} form
   * @param {ProgressBar} missionProgressBar - Shows how far through the tutorial the user is.
   * @param {PanoOverlayControls} panoOverlayControls
   * @param {LabelModes} labelModes - Held shut, letting one label type through per step.
   * @param {RibbonMenu} ribbon - Whose button for that type is lit.
   * @param {Canvas} canvas - Says where each label the labeler places landed.
   * @param {ContextMenu} contextMenu
   * @param {ZoomControl} zoomControl
   * @param {Tracker} tracker
   */
  constructor(ui, frame, onboardingStates, minimap, panoManager, navArrows, compass, navigationService,
    missionContainer, labelContainer, observedArea, form, missionProgressBar, panoOverlayControls, labelModes, ribbon,
    canvas, contextMenu, zoomControl, tracker) {
    this.#canvas = canvas;
    this.#ui = ui;
    this.#frame = frame;
    this.#minimap = minimap;
    this.#panoManager = panoManager;
    this.#navArrows = navArrows;
    this.#compass = compass;
    this.#navigationService = navigationService;
    this.#missionContainer = missionContainer;
    this.#labelContainer = labelContainer;
    this.#observedArea = observedArea;
    this.#form = form;
    this.#missionProgressBar = missionProgressBar;
    this.#panoOverlayControls = panoOverlayControls;
    this.#labelModes = labelModes;
    this.#ribbon = ribbon;
    this.#tracker = tracker;
    this.#uiCanvas = ui.canvas;
    this.#contextMenu = contextMenu;
    this.#uiOnboarding = ui.onboarding;
    this.#zoomControl = zoomControl;

    this.#states = onboardingStates.get();
    this.#statesWithProgress = this.#states.filter((state) => state.progression);
  }

  start() {
    this.#tracker.push('Onboarding_Start');

    this.#adjustMap();

    const retakeButton = document.getElementById('navbar-retake-tutorial-btn');
    if (retakeButton) retakeButton.style.display = 'none';

    const canvasUI = /** @type {HTMLCanvasElement} */ (this.#uiOnboarding.canvas);
    if (canvasUI) {
      this.#ctx = canvasUI.getContext('2d');
      // Render at on-screen/HiDPI resolution while drawing stays in the 720x480 logical frame, like the main label
      // canvas — a CSS-stretched 720x480 bitmap makes the example labels visibly pixelated (#4817).
      util.sizeCanvasToDisplay(canvasUI, this.#ctx);
    }
    this.#uiOnboarding.holder.style.visibility = 'visible';

    this.#navArrows.showing.hold('tutorial');
    this.#labelContainer.deleting.hold('tutorial');

    this.#navigationService.walking.hold('tutorial');
    this.#zoomControl.zoomInGate.hold('tutorial');
    this.#zoomControl.zoomOutGate.hold('tutorial');
    this.#labelModes.switching.hold('tutorial');
    this.#panoOverlayControls.disableButtons();

    this.#compass.hideMessage();
    this.#compass.click.hold('tutorial');

    this.#contextMenu.disableRatingSeverity();
    this.#contextMenu.disableTagging();

    this.#visit(this.#getState('initialize'));
  }

  /**
   * Swaps the live minimap for the tutorial's fixed screenshot, keeping the markers and fog drawn over it.
   */
  #adjustMap() {
    const holder = this.#ui.minimap.holder;
    // Render the minimap at its native square size and zoom the whole holder uniformly (see .minimap-tutorial) so
    // the static screenshot, the label markers, and the fog all share one coordinate frame and stay aligned.
    holder.classList.add('minimap-tutorial');
    Object.assign(holder.style, {
      backgroundImage: `url('${util.assetPath('images/explore/onboarding/TutorialMiniMap.jpg')}')`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    });

    // Fit the square to the available sidebar height now and whenever the sidebar resizes (e.g. UI-scale changes).
    this.#sizeTutorialMinimap();
    if (window.ResizeObserver && !this.#tutorialMinimapResizeObserver) {
      this.#tutorialMinimapResizeObserver = new ResizeObserver(() => this.#sizeTutorialMinimap());
      this.#tutorialMinimapResizeObserver.observe(holder.parentElement);
    }

    // The screenshot is the tutorial's map; the live basemap would show through it, offset and at another scale.
    this.#minimap.setBasemapVisible(false);
  }

  /**
   * Sizes the tutorial minimap to the largest square that fits the sidebar space below the region heading.
   *
   * The minimap renders at a native square size and is zoomed up uniformly, which keeps the screenshot, markers,
   * and fog aligned. We cap that zoom at the available height so the whole rounded square stays visible and
   * the peg stays centered, rather than overflowing the sidebar and getting clipped.
   */
  #sizeTutorialMinimap() {
    const holder = this.#ui.minimap.holder;
    const sidebar = document.getElementById('explore-sidebar');
    if (!holder || !sidebar) return;

    const baseSize = parseFloat(getComputedStyle(holder).getPropertyValue('--minimap-base-size'));
    const uiScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1;

    // The holder sits just below the heading; its space runs from its top down to the bottom of the sidebar. Its
    // top is anchored at the (unscaled) heading above it, so it's stable regardless of the transform we apply.
    const availableWidth = holder.parentElement.getBoundingClientRect().width;
    const availableHeight = sidebar.getBoundingClientRect().bottom - holder.getBoundingClientRect().top;
    if (availableWidth <= 0 || availableHeight <= 0) return;

    // Stretch horizontally to fill the column's full width, but cap the vertical scale at what fits the available
    // height (less 2px so the bottom corners stay visible). When the space is shorter than it is wide this squishes
    // the minimap a little, which is an acceptable trade for using the full width. The whole holder is scaled, so
    // the screenshot, markers, peg, and fog all stretch together and stay aligned with one another.
    const scaleX = availableWidth / baseSize;
    const scaleY = Math.min(uiScale, (availableHeight - 2) / baseSize);
    holder.style.transform = `scale(${scaleX}, ${scaleY})`;
    holder.style.transformOrigin = 'top left';
  }

  /**
   * Clear the onboarding canvas.
   * @returns {Onboarding}
   */
  clear() {
    if (this.#ctx) this.#ctx.clearRect(0, 0, this.#frame.width, this.#frame.height);
    return this;
  }

  /**
   * Re-rasterizes the onboarding canvas to the current displayed pano size and redraws the current state's
   * annotations. Call after the UI scale changes (e.g. on window resize), mirroring Canvas.resize().
   */
  resize() {
    const canvasUI = /** @type {HTMLCanvasElement} */ (this.#uiOnboarding.canvas);
    if (!canvasUI || !this.#ctx) return;
    util.sizeCanvasToDisplay(canvasUI, this.#ctx);

    // Re-sizing the bitmap wiped the canvas; redraw like the pov_changed listener in #visit does — under the same
    // conditions #visit draws, so a resize can't put annotations back on a state that is meant to have none. The
    // terminal state is the one that matters: it sets #currentState and then leaves the canvas cleared while the
    // submit-and-reload it kicked off is still in flight.
    const state = this.#currentState;
    if (!state || 'end-onboarding' in state) return;
    if (this.#onboardingStateAnnotationExists(state) || this.#savedAnnotations.length > 0) {
      this.#removeFlashingFromArrow();
      this.#drawAnnotations(state);
    }
  }

  /**
   * Draw a label on the onboarding canvas. Draws only static labels as examples in the tutorial.
   * @param {string} labelType - Label type that selects the correct icon
   * @param {number} x - Canvas x-position of the center of the label
   * @param {number} y - Canvas y-position of the center of the label
   * @param {number} [progress=1] - Entrance progress in [0, 1]; the icon fades and scales up as it pops in.
   */
  #drawStaticLabel(labelType, x, y, progress = 1) {
    if (this.#ctx) {
      this.#ctx.save();
      // Entrance pop: ease out while growing from 60% and fading in.
      const eased = 1 - Math.pow(1 - progress, 3);
      this.#ctx.globalAlpha = eased;
      this.#ctx.translate(x, y);
      this.#ctx.scale(0.6 + 0.4 * eased, 0.6 + 0.4 * eased);
      this.#ctx.translate(-x, -y);
      Label.renderLabelIcon(this.#ctx, labelType, x, y, this.#frame.iconRadius);
      this.#ctx.restore();
    }
  }

  /**
   * Draw a box on the onboarding canvas.
   * @param {number} x - Top-left x coordinate
   * @param {number} y - Top-left y coordinate
   * @param {number} width - Pixel width
   * @param {number} height - Pixel height
   * @param {Record<string, any>} parameters - Parameters
   */
  #drawBox(x, y, width, height, parameters) {
    if (this.#ctx) {
      this.#ctx.save();
      this.#ctx.strokeStyle = parameters.strokeStyle;
      this.#ctx.lineWidth = parameters.lineWidth;
      this.#ctx.strokeRect(x, y, width, height);
      this.#ctx.restore();
    }
    return this;
  }

  /**
   * Draw an arrow on the onboarding canvas.
   * @param {number} x1 - Starting x coordinate
   * @param {number} y1 - Starting y coordinate
   * @param {number} x2 - Ending x coordinate
   * @param {number} y2 - Ending y coordinate
   * @param {Record<string, any>} parameters - Parameters
   * @returns {Onboarding}
   */
  #drawArrow(x1, y1, x2, y2, parameters) {
    const ctx = this.#ctx;
    if (ctx) {
      const lineWidth = parameters.lineWidth;
      const lineCap = parameters.lineCap;
      const arrowWidth = parameters.arrowWidth;
      const strokeStyle = parameters.strokeStyle;
      let fill = parameters.fill;

      if (!parameters.fill) {
        fill = 'rgba(255,255,255,1)';
      }

      const dx = x2 - x1;
      const dy = y2 - y1;
      const theta = Math.atan2(dy, dx);

      ctx.save();
      ctx.fillStyle = fill;
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = lineCap;

      ctx.translate(x1, y1);
      ctx.beginPath();
      ctx.moveTo(arrowWidth * Math.sin(theta), -arrowWidth * Math.cos(theta));
      ctx.lineTo(dx + arrowWidth * Math.sin(theta), dy - arrowWidth * Math.cos(theta));

      // Draw an arrow head
      ctx.lineTo(dx + 3 * arrowWidth * Math.sin(theta), dy - 3 * arrowWidth * Math.cos(theta));
      ctx.lineTo(dx + 3 * arrowWidth * Math.cos(theta), dy + 3 * arrowWidth * Math.sin(theta));
      ctx.lineTo(dx - 3 * arrowWidth * Math.sin(theta), dy + 3 * arrowWidth * Math.cos(theta));

      ctx.lineTo(dx - arrowWidth * Math.sin(theta), dy + arrowWidth * Math.cos(theta));
      ctx.lineTo(-arrowWidth * Math.sin(theta), +arrowWidth * Math.cos(theta));

      ctx.fill();
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
    return this;
  }

  #drawBlinkingArrow(x1, y1, x2, y2, parameters, blink_frequency_modifier) {
    const max_frequency = 60 * blink_frequency_modifier;
    const blink_period = 0.5;
    const originalFillColor = parameters.fill;

    const helperBlinkingArrow = () => {
      this.#blinkTimer = (this.#blinkTimer + 1) % max_frequency;
      if (this.#blinkTimer < blink_period * max_frequency) {
        parameters.fill = originalFillColor;
      } else {
        parameters.fill = 'white';
      }
      this.#drawArrow(x1, y1, x2, y2, parameters);

      // requestAnimationFrame usually calls the function argument at the refresh rate of the screen (max_frequency)
      // Assume this is 60fps. We want to have an arrow flashing period of 0.5s (blink period)
      const function_identifier = window.requestAnimationFrame(helperBlinkingArrow);
      this.#blinkFunctionIdentifier.push(function_identifier);
    };
    helperBlinkingArrow();
  }

  #removeFlashingFromArrow() {
    while (this.#blinkFunctionIdentifier.length !== 0) {
      window.cancelAnimationFrame(this.#blinkFunctionIdentifier.pop());
    }
  }

  #stopAllBlinking() {
    this.#minimap.stopBlinkingMinimap();
    this.#compass.stopBlinking();
    this.#zoomControl.stopBlinking();
    this.#panoOverlayControls.stopBlinkingStuckButton();
  }

  #drawAnnotations(state) {
    let imX;
    let imY;
    let lineLength;
    let lineAngle;
    let x1;
    let x2;
    let y1;
    let y2;
    let centeredPov;
    let params;
    let i;
    let len;

    const currentPov = this.#panoManager.getPov();

    this.clear();

    // Get the full list of annotations, including those from previous states that should remain. Deduped because a
    // state is redrawn repeatedly (per pano move, per entrance frame) and each pass rebuilds #savedAnnotations from
    // this list — see util.misc.mergeOnboardingAnnotations for what that costs when it isn't (#4832).
    const currAnnotations = util.misc.mergeOnboardingAnnotations(this.#savedAnnotations, state.annotations);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let newLabelCount = 0;
    let entranceRunning = false;

    let blink_frequency_modifier = 0;
    for (i = 0, len = currAnnotations.length; i < len; i++) {
      if (currAnnotations[i].type === 'arrow') {
        blink_frequency_modifier = blink_frequency_modifier + 1;
      }
    }

    for (const annotation of currAnnotations) {
      imX = annotation.x;
      imY = annotation.y;

      // Decode the annotation's angular coordinate (see OnboardingStates.js) and map it to a canvas coordinate.
      imX = util.misc.unwrapPanoX(imX, currentPov.heading, Onboarding.TUTORIAL_PANO_WIDTH);
      centeredPov = util.pano.horizonRelativeCoordToPov(
        imX, imY, Onboarding.TUTORIAL_PANO_WIDTH, Onboarding.TUTORIAL_PANO_HEIGHT,
      );
      const canvasCoord = util.pano.centeredPovToCanvasCoord(
        centeredPov, currentPov, this.#frame.width, this.#frame.height, this.#frame.iconRadius,
      ) || { x: null, y: null };
      const onCanvas = canvasCoord.x !== null;

      if (annotation.type === 'arrow') {
        if (!onCanvas) continue;
        lineLength = annotation.length;
        lineAngle = annotation.angle;
        x2 = canvasCoord.x;
        y2 = canvasCoord.y;
        x1 = x2 - lineLength * Math.sin(util.math.toRadians(lineAngle));
        y1 = y2 - lineLength * Math.cos(util.math.toRadians(lineAngle));

        // The color of the arrow will by default alternate between white and the fill specified in annotation.
        params = {
          lineWidth: 1,
          fill: annotation.fill,
          lineCap: 'round',
          arrowWidth: 6,
          strokeStyle: 'rgba(0, 0, 0, 1)',
        };

        if (annotation.fill === null || annotation.fill === undefined || annotation.fill === 'white') {
          this.#drawArrow(x1, y1, x2, y2, params);
        } else {
          this.#drawBlinkingArrow(x1, y1, x2, y2, params, blink_frequency_modifier);
        }
      } else if (annotation.type === 'box') {
        if (!onCanvas) continue;
        params = {
          lineWidth: 4,
          strokeStyle: 'rgba(255, 255, 255, 1)',
        };
        this.#drawBox(canvasCoord.x, canvasCoord.y, annotation.width, annotation.height, params);
      } else if (annotation.type === 'label') {
        // Example labels pop in staggered, in the order the copy introduces them; POV/resize redraws after the
        // entrance render them at rest. Under prefers-reduced-motion they appear at rest immediately.
        if (annotation.appearStart === undefined) {
          annotation.appearStart = reduceMotion ? -Infinity : performance.now() + newLabelCount * 350;
          newLabelCount += 1;
        }
        const progress = Math.min(Math.max((performance.now() - annotation.appearStart) / 300, 0), 1);
        if (progress < 1) entranceRunning = true;
        // Only draw the label icon when it's on-screen; the minimap marker is still created below regardless.
        if (onCanvas && progress > 0) {
          this.#drawStaticLabel(annotation.labelType, canvasCoord.x, canvasCoord.y, progress);
        }
        // Create the minimap marker when the label's entrance slot arrives, so the map echoes the sequence.
        if (!annotation.firstDraw && progress > 0) {
          Label.createMinimapMarker(this.#minimap, annotation.labelType, { lat: annotation.lat, lng: annotation.lng });
          annotation.firstDraw = true;
        }
      }
    }

    // Keep redrawing while example labels are still popping in.
    window.cancelAnimationFrame(this.#labelIntroRafId);
    if (entranceRunning) {
      this.#labelIntroRafId = window.requestAnimationFrame(() => this.#drawAnnotations(state));
    }

    // Save any annotations that should be sticking around.
    this.#savedAnnotations = util.misc.carryOverOnboardingAnnotations(currAnnotations, state.id);
  }

  /**
   * @param {string} stateId - An id from OnboardingStates.js.
   * @returns {object} The state with that id.
   */
  #getState(stateId) {
    const found = this.#states.find((state) => state.id === stateId);
    if (!found) throw new Error(`Onboarding has no state with id "${stateId}".`);
    return found;
  }

  /**
   * Hide the message box.
   */
  #hideMessage() {
    if (this.#floatingCleanup) {
      this.#floatingCleanup();
      this.#floatingCleanup = null;
    }
    this.#uiOnboarding.messageHolder.style.display = 'none';
  }

  /**
   * Resolve and visit the next state, passing `thisArg` through to a function-valued transition.
   * @param {string|Function} nextState - State id, or a function returning a state id.
   * @param {object} [params] - Parameters the transition function may read.
   * @param {EventTarget|Label} [thisArg] - The `this` for a function-valued transition: the element the user interacted
   *     with, or the label being tagged.
   */
  #transitionTo(nextState, params, thisArg) {
    const stateId = typeof nextState === 'function' ? nextState.call(thisArg, params) : nextState;
    this.#visit(this.#getState(stateId));
  }

  /**
   * On-screen rect of a placed label, for use as a Floating UI virtual reference.
   *
   * The projection is left unbounded and the result clamped into the pano instead, so a label the user has panned
   * off-screen keeps a rect that rides the near edge.
   *
   * @param {Label} label - The label to measure.
   * @returns {DOMRect|null} null if there is no such label or its position can't be projected.
   */
  #labelRect(label) {
    const pane = document.getElementById('street-view-holder');
    const centeredPov = label && !label.isDeleted() && label.getProperty('povOfLabelIfCentered');
    if (!pane || !centeredPov) return null;

    const coord = util.pano.centeredPovToCanvasCoord(
      centeredPov, this.#panoManager.getPov(), this.#frame.width, this.#frame.height, Infinity,
    );
    if (!coord) return null;

    const scale = util.exploreDisplayScale();
    const iconSize = this.#frame.iconRadius * scale * 2;
    const paneRect = pane.getBoundingClientRect();
    const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));
    const iconRect = new DOMRect(
      clamp(paneRect.left + coord.x * scale - iconSize / 2, paneRect.left, paneRect.right - iconSize),
      clamp(paneRect.top + coord.y * scale - iconSize / 2, paneRect.top, paneRect.bottom - iconSize),
      iconSize, iconSize,
    );

    const card = this.#uiCanvas.hoverCard;
    const cardShown = label.getHoverInfoVisibility() === 'visible' && getComputedStyle(card).visibility === 'visible';
    if (!cardShown) return iconRect;

    const cardRect = card.getBoundingClientRect();
    const left = Math.min(iconRect.left, cardRect.left);
    const top = Math.min(iconRect.top, cardRect.top);
    return new DOMRect(
      left, top, Math.max(iconRect.right, cardRect.right) - left, Math.max(iconRect.bottom, cardRect.bottom) - top,
    );
  }

  /**
   * Builds an anchor resolver that points the message at a placed label.
   *
   * @param {Label} label - The label to point at.
   * @returns {Function} Resolver for #anchorMessageTo.
   */
  #labelAnchor(label) {
    return () => {
      const rect = this.#labelRect(label);
      return rect ? { rect, placement: 'top', boundedByPane: true } : null;
    };
  }

  /**
   * Builds an anchor resolver that points the message at a UI element.
   *
   * A callout anchored inside the context menu follows the menu's label while the menu is closed: the menu is
   * hidden with `visibility`, so it keeps a rect the callout would otherwise go on pointing at (#4859).
   *
   * @param {string} selector - CSS selector of the element to point the box at.
   * @param {string} placement - Preferred Floating UI placement (e.g. 'left', 'right', 'top', 'bottom').
   * @returns {Function} Resolver for #anchorMessageTo.
   */
  #elementAnchor(selector, placement) {
    return () => {
      const el = document.querySelector(selector);
      if (!el) return null;

      if (!this.#contextMenu.isOpen() && this.#ui.contextMenu.holder?.contains(el)) {
        const rect = this.#labelRect(this.#contextMenu.getTargetLabel());
        if (rect) return { rect, placement: 'top', boundedByPane: true };
      }
      const pane = document.getElementById('street-view-holder');
      return { rect: el.getBoundingClientRect(), placement, boundedByPane: Boolean(pane?.contains(el)) };
    };
  }

  /**
   * Position the onboarding message box beside its anchor using Floating UI lib, with an arrow pointing at it.
   * @param {Function} resolveAnchor - Returns {rect, placement, boundedByPane} for the current frame, or null.
   */
  #anchorMessageTo(resolveAnchor) {
    const floating = this.#uiOnboarding.messageHolder;
    let anchor = resolveAnchor();
    if (!anchor || !floating) return;

    // The arrow Floating UI positions, built fresh since showMessage has just rewritten the box's contents.
    const arrowEl = document.createElement('div');
    arrowEl.className = 'fui-arrow';
    floating.appendChild(arrowEl);
    floating.style.position = 'absolute';

    const pane = document.getElementById('street-view-holder');
    // A virtual reference, because an anchor can be a label icon painted on the canvas, with no element of its own.
    const reference = { getBoundingClientRect: () => (resolveAnchor() ?? anchor).rect, contextElement: pane };

    // Recompute on scroll/resize/layout changes so the box keeps tracking the element.
    const update = () => {
      anchor = resolveAnchor() ?? anchor;
      // The arrow is a square rotated 45deg centered on the box edge, so its tip protrudes by half its diagonal.
      // Gap the box from the element by that distance so the tip just reaches the near edge. Re-measured each
      // update since the arrow scales with --ui-scale.
      const arrowHalf = arrowEl.offsetWidth / 2;
      const arrowProtrusion = arrowHalf * Math.SQRT2;
      // Confine the callout to the streetscape pane so the pane's border can't slice it — but only when its
      // anchor lives in the pane; minimap/sidebar anchors need the callout to follow them across the gutter.
      const boundary = anchor.boundedByPane ? pane : undefined;
      computePosition(reference, floating, {
        placement: anchor.placement,
        middleware: [
          offset(arrowProtrusion),
          // bestFit: when no side fully fits the callout, take the roomiest one rather than the preferred one.
          flip({ boundary, fallbackStrategy: 'bestFit' }),
          shift({ boundary, padding: 8 }),
          // The callout must never cover the control it points at: instead of sliding over the anchor to stay in
          // the pane, shrink to the width the chosen side actually has and let the text wrap taller. Runs after
          // flip/shift per the size() contract; autoUpdate re-runs positioning after the resize settles.
          size({
            boundary,
            padding: 8,
            apply({ availableWidth, elements }) {
              elements.floating.style.maxWidth = `${Math.max(0, availableWidth)}px`;
            },
          }),
        ],
      }).then(({ x, y, placement: finalPlacement }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px`, transform: 'none' });

        // Point the arrow at the center of the anchor's near edge, computed from live rects so it stays centered even
        // when shift() nudged the box along that axis. Clamped to the box's own edge, inset by the arrow's half-width.
        const side = finalPlacement.split('-')[0];
        const staticSide = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' }[side];
        const refRect = anchor.rect;
        const floatRect = floating.getBoundingClientRect();
        const along = side === 'left' || side === 'right'
          ? { prop: 'top', center: refRect.top + refRect.height / 2 - floatRect.top, boxLen: floatRect.height }
          : { prop: 'left', center: refRect.left + refRect.width / 2 - floatRect.left, boxLen: floatRect.width };
        const maxOffset = Math.max(arrowHalf, along.boxLen - 3 * arrowHalf);
        Object.assign(arrowEl.style, { left: '', top: '', right: '', bottom: '' });
        arrowEl.style[along.prop] = `${Math.min(Math.max(along.center - arrowHalf, arrowHalf), maxOffset)}px`;
        arrowEl.style[staticSide] = `${-arrowHalf}px`;
      });
    };

    // animationFrame: an anchor can be a canvas-painted label, or a menu section that goes visibility:hidden —
    // neither moves in a way the observers autoUpdate uses by default can see.
    this.#floatingCleanup = autoUpdate(reference, floating, update, { animationFrame: true });
  }

  /**
   * Pins the message box just above an annotation coordinate (a step's annotation arrow), so the instruction and
   * the arrow read as one unit. Computed once per message: the steps that use this clamp panning to a few
   * degrees, so the arrow cannot drift far from the box.
   * @param {{x: number, y: number}} panoCoord - Angular annotation coordinates (see OnboardingStates.js).
   */
  #positionMessageAtPanoCoord(panoCoord) {
    const currentPov = this.#panoManager.getPov();
    const imX = util.misc.unwrapPanoX(panoCoord.x, currentPov.heading, Onboarding.TUTORIAL_PANO_WIDTH);
    const centeredPov = util.pano.horizonRelativeCoordToPov(
      imX, panoCoord.y, Onboarding.TUTORIAL_PANO_WIDTH, Onboarding.TUTORIAL_PANO_HEIGHT,
    );
    const canvasCoord = util.pano.centeredPovToCanvasCoord(
      centeredPov, currentPov, this.#frame.width, this.#frame.height, this.#frame.iconRadius,
    );
    if (!canvasCoord || canvasCoord.x === null) return; // Off-screen: keep the default top-left position.

    const scale = util.exploreDisplayScale();
    const holder = this.#uiOnboarding.messageHolder;
    holder.classList.add('onboarding-message-pano-anchored');
    // Center on the arrow, clamped inside the pane; the class translates the box up so its bottom edge lands
    // just above the arrow's tail (annotation arrows point down at their target).
    const EDGE_PADDING = 8;    // Keeps the box off the pane's rounded corners, matching the Floating UI callouts.
    const ARROW_CLEARANCE = 60; // Logical-frame gap above the arrow tip, so the box clears the arrow's full length.
    const paneWidth = util.EXPLORE_CANVAS_WIDTH * scale;
    const boxWidth = holder.getBoundingClientRect().width;
    const left = Math.min(
      Math.max(canvasCoord.x * scale - boxWidth / 2, EDGE_PADDING), paneWidth - boxWidth - EDGE_PADDING,
    );
    const top = Math.max((canvasCoord.y - ARROW_CLEARANCE) * scale, EDGE_PADDING);
    holder.style.left = `${left}px`;
    holder.style.top = `${top}px`;
  }

  /**
   * Show a message box.
   * @param {Record<string, any>} parameters
   */
  #showMessage(parameters) {
    const message = parameters.message;
    const messageHolder = this.#uiOnboarding.messageHolder;
    // Flash the box yellow once to catch the user's attention.
    messageHolder.classList.toggle('onboarding-message-flash');
    setTimeout(() => messageHolder.classList.toggle('onboarding-message-flash'), 100);

    // Reset positioning state so each message starts clean.
    messageHolder.classList.remove('callout-floating', 'onboarding-message-takeover', 'onboarding-message-fullpage',
      'onboarding-message-pano-anchored', 'onboarding-message-pass-through');
    Object.assign(messageHolder.style, { position: '', top: '', left: '', transform: '', width: '', maxWidth: '' });
    this.#uiOnboarding.background.style.visibility = 'hidden';

    messageHolder.style.display = '';

    // Width is authored in logical (pre-scale) pixels; scale it to on-screen pixels.
    if ('width' in parameters) {
      messageHolder.style.width = `${parameters.width * util.exploreDisplayScale()}px`;
    }

    messageHolder.innerHTML = typeof message === 'function' ? message() : message;

    // Place the message as a takeover, a callout on an anchor, or above a pano point; otherwise it keeps its default
    // top-left corner.
    if (parameters.background) {
      // Takeover: dim the viewport behind the message. Centered by default; fullPage opts into a full-page canvas.
      this.#uiOnboarding.background.style.visibility = 'visible';
      messageHolder.classList.add('onboarding-message-takeover');
      if (parameters.fullPage) messageHolder.classList.add('onboarding-message-fullpage');
    } else if (parameters.anchor || parameters.labelAnchor) {
      // Anchor to a live UI element or a placed label; Floating UI computes the position and arrow.
      messageHolder.classList.add('callout-floating');
      if (parameters.labelAnchor) {
        // Whatever this callout ends up over, it must not intercept the hover or click the step is asking for.
        messageHolder.classList.add('onboarding-message-pass-through');
      }
      this.#anchorMessageTo(parameters.labelAnchor
        ? this.#labelAnchor(this.#labelContainer.getLatestLabel())
        : this.#elementAnchor(parameters.anchor, parameters.placement || 'right'));
    } else if (parameters.panoAnchor) {
      // Pin just above a pano-image coordinate (e.g. the step's annotation arrow).
      this.#positionMessageAtPanoCoord(parameters.panoAnchor);
    }
  }

  /**
   * Marks the tutorial mission complete, then saves and reloads into a real Explore mission.
   */
  #endTheOnboarding() {
    this.#tracker.push('Onboarding_End');
    this.#missionContainer.getCurrentMission().setProperty('isComplete', true);

    // Makes sure all data has been submitted to server, then refreshes the page.
    this.#form.submitData().then(() => {
      window.location.replace('/explore');
    });
  }

  #onboardingStateAnnotationExists(state) {
    return 'annotations' in state && state.annotations;
  }

  #onboardingStateMessageExists(state) {
    return 'message' in state && state.message;
  }

  #blinkInterface(state) {
    // Blink parts of the interface.
    if ('blinks' in state.properties && state.properties.blinks) {
      const len = state.properties.blinks.length;
      for (let i = 0; i < len; i++) {
        switch (state.properties.blinks[i]) {
          case 'minimap':
            this.#minimap.blinkMinimap();
            break;
          case 'compass':
            this.#compass.blink();
            break;
          case 'stuck':
            this.#panoOverlayControls.blinkStuckButton();
            break;
          case 'movement-arrow':
            this.#navArrows.blink();
            break;
        }
      }
    }
  }

  /**
   * Execute an instruction based on the current state.
   * @param {Record<string, any>} state
   */
  #visit(state) {
    // Update the progress bar (if the state marks progress in the tutorial) & log the transition to the new state.
    const stepNum = this.#statesWithProgress.findIndex((s) => s.id === state.id);
    if (stepNum !== -1 && !state.visited) {
      const completionRate = stepNum / this.#statesWithProgress.length;
      this.#missionProgressBar.update(completionRate);
      this.#tracker.push('Onboarding_Transition', { onboardingTransition: state.id, step: stepNum });
    } else {
      this.#tracker.push('Onboarding_Transition', { onboardingTransition: state.id });
    }
    state.visited = true;
    this.#currentState = state;

    let annotationListener;

    this.clear(); // Clear whatever was rendered on the onboarding-canvas in the previous state.
    this.#removeFlashingFromArrow();

    // End the onboarding if there is no transition state is specified. Move to the actual task
    if ('end-onboarding' in state) {
      this.#endTheOnboarding();
      return;
    } else {
      this.#hideMessage();
    }

    // A state's `properties` is either an object or a one-element array of them (see the dispatch below), so read
    // both `action` and anything beside it off the same resolved object rather than off `state.properties`.
    const properties = Array.isArray(state.properties) ? state.properties[0] : state.properties;
    const action = properties?.action;

    // Select-a-label-type steps anchor their callout to the ribbon button they ask the user to click, so the
    // instruction and the pulsing button read as one unit. Derived here so the selector stays single-sourced.
    if ((action === 'SelectLabelType' || action === 'RedoSelectLabelType') && state.message) {
      state.message.anchor = `.label-type-button-holder[val="${properties.labelType}"]`;
      state.message.placement = 'bottom';
    }

    // Label-placement steps pin their message just above the arrow they narrate, derived from the state's own
    // arrow annotation so the two can't drift apart.
    if (action === 'LabelAccessibilityAttribute' && state.message && state.annotations) {
      const arrow = state.annotations.find((a) => a.type === 'arrow');
      if (arrow) state.message.panoAnchor = { x: arrow.x, y: arrow.y };
    }

    // Delete steps point their message at the misplaced label the user has to hover (#4859).
    if (action === 'DeleteAccessibilityAttribute' && state.message) {
      state.message.labelAnchor = true;
    }

    // Show user a message box.
    if (this.#onboardingStateMessageExists(state)) {
      this.#showMessage(state.message);
    }

    // Draw arrows to annotate target accessibility attributes
    if (this.#onboardingStateAnnotationExists(state) || this.#savedAnnotations.length > 0) {
      this.#drawAnnotations(state);
      annotationListener = google.maps.event.addListener(this.#panoManager.panoViewer.gsvPano, 'pov_changed', () => {
        // Stop the animation for the blinking arrows.
        this.#removeFlashingFromArrow();
        this.#drawAnnotations(state);
      });
    }

    // Change behavior based on the current state.
    if ('properties' in state) {
      // Remove blinking if necessary.
      if (state.properties.stopBlinking) {
        this.#stopAllBlinking();
      }

      if (state.properties.constructor === Array) {
        // Restrict panning.
        this.#panoManager.setHeadingRange({
          min: state.properties[0].minHeading, max: state.properties[0].maxHeading,
        });

        // Ideally we need a for loop that goes through every element of the property array and calls the
        // corresponding action's handler. Not just the label accessibility attribute's handler.
        if (state.properties[0].action === 'LabelAccessibilityAttribute') {
          this.#visitLabelAccessibilityAttributeState(state, annotationListener);
        }
      } else {
        // Restrict panning.
        this.#panoManager.setHeadingRange({ min: state.properties.minHeading, max: state.properties.maxHeading });
        if (state.properties.action === 'Introduction') {
          this.#visitIntroduction(state, annotationListener);
        } else if (state.properties.action === 'SelectLabelType' || state.properties.action === 'RedoSelectLabelType') {
          this.#visitSelectLabelTypeState(state, annotationListener);
        } else if (state.properties.action === 'DeleteAccessibilityAttribute') {
          this.#visitDeleteAccessibilityAttributeState(state, annotationListener);
          this.#contextMenu.hide();
        } else if (state.properties.action === 'Zoom') {
          this.#visitZoomState(state, annotationListener);
        } else if (state.properties.action === 'RateSeverity' || state.properties.action === 'RedoRateSeverity') {
          this.#visitRateSeverity(state, annotationListener);
        } else if (state.properties.action === 'AddTag' || state.properties.action === 'RedoAddTag') {
          this.#visitAddTag(state, annotationListener);
        } else if (state.properties.action === 'AdjustHeadingAngle') {
          this.#visitAdjustHeadingAngle(state, annotationListener);
        } else if (state.properties.action === 'WalkTowards') {
          this.#visitWalkTowards(state, annotationListener);
        } else if (state.properties.action === 'Instruction') {
          this.#visitInstruction(state, annotationListener);
        }
      }
    }
  }

  /**
   * Position the pano at the tutorial's opening POV, then advance to the first interactive step.
   *
   * The welcome/skip UI lives in the pre-tutorial intro (TutorialIntro), whose "Start Mission" button leads here, so
   * this state is non-interactive: it just sets the POV and moves on.
   *
   * @param {Record<string, any>} state - The 'initialize' state from OnboardingStates.js.
   * @param {google.maps.MapsEventListener} [listener] - A Google Maps event listener to remove before advancing.
   */
  #visitIntroduction(state, listener) {
    if (listener) google.maps.event.removeListener(listener);
    this.#panoManager.setPov({
      heading: state.properties.heading,
      pitch: state.properties.pitch,
      zoom: state.properties.zoom,
    });
    // The jump to the opening POV isn't user panning; don't let it pre-reveal fog or credit the examined ring.
    this.#observedArea.resetCurrentPano();
    this.#transitionTo(state.transition);
  }

  /**
   * Called when the user is told to click on the compass or nav arrows to move to the next image.
   * @param {Record<string, any>} state - The current state defined in OnboardingStates.js
   * @param {google.maps.MapsEventListener} [listener] - An optional listener on a Google Maps event, to be removed
   *     before moving to the next state
   */
  #visitWalkTowards(state, listener) {
    const nextPanoId = 'afterWalkTutorial';

    // Add a link to the second pano so that the user can click on it.
    this.#navArrows.showing.release('tutorial');
    this.#navArrows.show();

    // A callback to disable walking after user has moved to 2nd pano, then moves to next state.
    const callback = () => {
      this.#navigationService.walking.hold('tutorial');
      this.#compass.detachMessageClickHandler(clickToNextPano);
      this.#navArrows.showing.hold('tutorial');
      this.#ui.streetview.navArrows.removeEventListener('click', clickToNextPano);
      if (listener) google.maps.event.removeListener(listener);
      this.#transitionTo(state.transition);
    };

    // Replace default behavior when clicking on the navigation message/arrows to move to the next pano.
    const clickToNextPano = () => {
      this.#navigationService.moveToPano(nextPanoId, true).then(callback);
    };

    // NavArrows' own click handler steps aside during the tutorial, so this one moves.
    this.#navigationService.walking.release('tutorial');
    this.#ui.streetview.navArrows.addEventListener('click', clickToNextPano);
    this.#compass.attachMessageClickHandler(clickToNextPano);

    this.#blinkInterface(state);
  }

  #visitAdjustHeadingAngle(state, listener) {
    let $target;
    this.#uiOnboarding.handGestureHolder.classList.remove('ps-hidden');

    const callback = () => {
      const pov = this.#panoManager.getPov();
      // Note that the tolerance is only a tolerance to the left. Must hit at least the given heading to proceed.
      if ((360 + state.properties.heading - pov.heading) % 360 < state.properties.tolerance) {
        google.maps.event.removeListener($target);
        if (listener) google.maps.event.removeListener(listener);
        this.#uiOnboarding.handGestureHolder.classList.add('ps-hidden');
        this.#transitionTo(state.transition);
      }
    };

    $target = google.maps.event.addListener(this.#panoManager.panoViewer.gsvPano, 'pov_changed', callback);
  }

  #visitRateSeverity(state, listener) {
    this.#contextMenu.enableRatingSeverityForTutorialLabel(state.properties.labelNumber);
    // Pulse the one section the tutorial has enabled; the message box is anchored to it as well.
    this.#ui.contextMenu.severityMenu.classList.add('onboarding-attention');
    const radios = this.#ui.contextMenu.radioButtons;
    const callback = (e) => {
      if (listener) google.maps.event.removeListener(listener);
      for (const radio of radios) radio.removeEventListener('change', callback);
      this.#contextMenu.disableRatingSeverity();
      this.#ui.contextMenu.severityMenu.classList.remove('onboarding-attention');
      this.#transitionTo(state.transition, undefined, e.currentTarget);
    };
    for (const radio of radios) radio.addEventListener('change', callback);
  }

  #visitAddTag(state, listener) {
    this.#contextMenu.enableTaggingForTutorialLabel(state.properties.labelNumber);
    // Pulse the one section the tutorial has enabled; the message box is anchored to it as well.
    this.#ui.contextMenu.tagSection.classList.add('onboarding-attention');
    const tagHolder = this.#ui.contextMenu.tagHolder;
    const callback = () => {
      if (listener) google.maps.event.removeListener(listener);
      tagHolder.removeEventListener('tagIds-updated', callback);
      this.#contextMenu.disableTagging();
      this.#ui.contextMenu.tagSection.classList.remove('onboarding-attention');
      this.#transitionTo(state.transition, undefined, this.#contextMenu.getTargetLabel());
    };
    // We use a custom event here to ensure that this is triggered after the tags have been updated.
    tagHolder.addEventListener('tagIds-updated', callback);
  }

  #visitInstruction(state, listener) {
    if (state === this.#getState('outro')) {
      this.#startCelebrationClip();
    }
    this.#blinkInterface(state);

    if (!('okButton' in state) || state.okButton) {
      // Insert an ok button.
      const okButtonText = util.escapeHTML(state.okButtonText || 'Ok');
      this.#uiOnboarding.messageHolder.insertAdjacentHTML('beforeend',
        `<div class='onboarding-ok-button-holder'>
          <button id='onboarding-ok-button' class='button button--medium button--secondary'>${okButtonText}</button>
        </div>`);
    }

    const okButton = document.getElementById('onboarding-ok-button');
    const callback = (e) => {
      if (listener) google.maps.event.removeListener(listener);
      okButton.removeEventListener('click', callback);
      if ('blinks' in state.properties && state.properties.blinks) {
        this.#stopAllBlinking();
      }
      this.#transitionTo(state.transition, undefined, e.currentTarget);
    };
    okButton?.addEventListener('click', callback);
  }

  /**
   * Starts the celebration clip on the just-injected tutorial-complete screen and wires up its pause control.
   *
   * The clip loops, so it needs an in-page way to stop it (WCAG 2.2.2). A visitor who has asked for reduced motion
   * gets it paused on its still — the clip's last frame — rather than a flash of motion that then stops.
   */
  #startCelebrationClip() {
    const screen = this.#uiOnboarding.messageHolder;
    const hero = screen.querySelector('.tutorial-complete__hero');
    const toggle = screen.querySelector('.motion-toggle');
    if (!hero || !toggle) return;

    // Set muted on the element too: the screen is injected as an HTML string, and the `muted` attribute doesn't
    // reliably carry over that way, which would leave play() blocked by the autoplay policy.
    hero.muted = true;

    let motionPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const applyMotionState = () => {
      toggle.classList.toggle('is-paused', motionPaused);
      toggle.setAttribute('aria-pressed', String(motionPaused));
      if (motionPaused) {
        hero.pause();
        hero.poster = hero.dataset.poster;
        return;
      }
      // Drop the still before playing: it's the clip's last frame, so leaving it up would flash the end of the
      // celebration over its opening frames while the clip loads.
      hero.removeAttribute('poster');
      hero.play().catch(() => {
        // Rejected when the browser refuses to play; the still stands in for the animation in that case.
        hero.poster = hero.dataset.poster;
      });
    };

    toggle.addEventListener('click', () => {
      motionPaused = !motionPaused;
      this.#tracker.push(motionPaused ? 'Onboarding_PauseAnimation' : 'Onboarding_PlayAnimation');
      applyMotionState();
    });
    applyMotionState();
  }

  /**
   * Blink the given label type and nudge them to click one of the buttons in the ribbon menu.
   * Move on to the next state if they click the button.
   * @param {Record<string, any>} state
   * @param {google.maps.MapsEventListener} [listener]
   */
  #visitSelectLabelTypeState(state, listener) {
    const labelType = state.properties.labelType;

    this.#labelModes.enableMode(labelType);
    this.#ribbon.startBlinking(labelType);

    // To handle when user presses ESC - disable mode only when the user places the label.
    this.#mouseDownCanvasDrawingHandler = () => {
      this.#labelModes.disableMode(labelType);
    };

    const stopListening = this.#labelModes.onModeChanged((mode) => {
      if (mode !== labelType) return;
      stopListening();
      this.#labelModes.enableMode('Walk');

      // Disable only when the user places the label
      this.#uiCanvas.drawingLayer.addEventListener('mousedown', this.#mouseDownCanvasDrawingHandler);

      this.#ribbon.stopBlinking();
      if (listener) google.maps.event.removeListener(listener);
      this.#transitionTo(state.transition);
    });
  }

  /**
   * Tell the user to zoom in/out.
   * @param {Record<string, any>} state
   * @param {google.maps.MapsEventListener} [listener]
   */
  #visitZoomState(state, listener) {
    const zoomType = state.properties.type;

    if (zoomType === 'in') {
      this.#zoomControl.blinkZoomIn();
      this.#zoomControl.zoomInGate.release('tutorial');
    } else {
      this.#zoomControl.blinkZoomOut();
      this.#zoomControl.zoomOutGate.release('tutorial');
    }

    const stopListening = this.#zoomControl.onZoomed((direction) => {
      if (direction !== zoomType) return;
      stopListening();
      this.#zoomControl.stopBlinking();
      if (zoomType === 'in') this.#zoomControl.zoomInGate.hold('tutorial');
      else this.#zoomControl.zoomOutGate.hold('tutorial');
      this.#labelModes.enableMode('Walk');

      if (listener) google.maps.event.removeListener(listener);
      this.#transitionTo(state.transition);
    });
  }

  /**
   * Tell the user to label the multiple possible target attributes.
   * @param {Record<string, any>} state
   * @param {google.maps.MapsEventListener} [listener]
   */
  #visitLabelAccessibilityAttributeState(state, listener) {
    const properties = state.properties[0];
    const transition = state.transition;

    // Checks whether the label was placed in the right spot.
    const stopListening = this.#canvas.onLabeled((label) => {
      stopListening();
      if (listener) google.maps.event.removeListener(listener);

      const panoX = label.getProperty('panoXY').x * Onboarding.TUTORIAL_PANO_SCALE_FACTOR;
      const panoY = label.getProperty('panoXY').y * Onboarding.TUTORIAL_PANO_SCALE_FACTOR;
      const imageX = properties.imageX;
      const imageY = properties.imageY;
      const tolerance = properties.tolerance;

      const distance = (imageX - panoX) * (imageX - panoX) + (imageY - panoY) * (imageY - panoY);

      // If the label was placed close enough to the target, move on to the next state.
      if (distance < tolerance * tolerance) {
        label.setProperty('tutorialLabelNumber', properties.labelNumber);

        this.#labelContainer.deleting.hold('tutorial');

        // Disable labeling mode.
        this.#labelModes.disableMode(label.getLabelType());
        this.#labelModes.enableMode('Walk');
        this.#uiCanvas.drawingLayer.removeEventListener('mousedown', this.#mouseDownCanvasDrawingHandler);

        this.#transitionTo(transition[0], { accurate: true });
      } else {
        this.#transitionTo(transition[0], { accurate: false });
      }
    });
  }

  /**
   * Tell the user to delete the label they placed that is far away from where they were supposed to place it.
   *
   * @param {Record<string, any>} state
   * @param {google.maps.MapsEventListener} [listener]
   */
  #visitDeleteAccessibilityAttributeState(state, listener) {
    this.#labelModes.disableMode(state.properties.labelType);
    this.#labelModes.enableMode('Walk');
    this.#labelContainer.deleting.release('tutorial');

    const stopListening = this.#labelContainer.onLabelRemoved(() => {
      stopListening();
      if (listener) google.maps.event.removeListener(listener);
      this.clear();
      this.#removeFlashingFromArrow(); // TODO remove this if it turns out that we don't need it.
      this.#transitionTo(state.transition);
    });
  }
}
